<script setup lang="ts">
import type { ComponentPublicInstance } from "vue";
import { ref } from "vue";

import { FILTERS, type FilterId } from "@/types/album";

/**
 * Port de `.filter-wrap` / `.filters` (design-reference/styles.css).
 *
 * Tablist ARIA avec tabindex baladeur : seul l'onglet actif est focusable au
 * clavier (Tab), les flèches gauche/droite (et Origine/Fin) déplacent le
 * focus et activent l'onglet visé — modèle d'activation automatique, adapté
 * puisque changer d'onglet ne fait ici que filtrer une grille déjà affichée.
 */

defineProps<{ modelValue: FilterId }>();
const emit = defineEmits<{ "update:modelValue": [value: FilterId] }>();

const tabRefs = ref<Array<HTMLButtonElement | null>>([]);

const setTabRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  tabRefs.value[index] = el instanceof HTMLButtonElement ? el : null;
};

const focusTab = (index: number): void => {
  const count = FILTERS.length;
  const next = ((index % count) + count) % count;
  const filter = FILTERS[next];
  if (!filter) return;
  emit("update:modelValue", filter.id);
  tabRefs.value[next]?.focus();
};

const onKeydown = (event: KeyboardEvent, index: number): void => {
  switch (event.key) {
    case "ArrowRight":
      event.preventDefault();
      focusTab(index + 1);
      break;
    case "ArrowLeft":
      event.preventDefault();
      focusTab(index - 1);
      break;
    case "Home":
      event.preventDefault();
      focusTab(0);
      break;
    case "End":
      event.preventDefault();
      focusTab(FILTERS.length - 1);
      break;
    default:
      break;
  }
};
</script>

<template>
  <div class="filter-wrap" data-testid="filter-bar">
    <div class="filters" role="tablist" aria-label="Filtrer les souvenirs">
      <button
        v-for="(filter, index) in FILTERS"
        :key="filter.id"
        :ref="(el) => setTabRef(el, index)"
        type="button"
        class="filter"
        :class="{ 'is-active': filter.id === modelValue }"
        :data-testid="`filter-${filter.id}`"
        role="tab"
        aria-controls="album-gallery"
        :aria-selected="filter.id === modelValue"
        :tabindex="filter.id === modelValue ? 0 : -1"
        @click="emit('update:modelValue', filter.id)"
        @keydown="onKeydown($event, index)"
      >
        {{ filter.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.filter-wrap {
  position: sticky;
  top: 68px;
  z-index: 35;
  margin: 0 -14px 20px;
  padding: 9px 14px;
  background: rgb(248 246 239 / 0.9);
  backdrop-filter: blur(13px);
}

.filters {
  display: flex;
  gap: 7px;
  max-width: 1120px;
  margin: auto;
  padding-bottom: 2px;
  overflow-x: auto;
  scrollbar-width: none;
}

.filters::-webkit-scrollbar {
  display: none;
}

.filter {
  flex: 0 0 auto;
  padding: 9px 14px;
  color: var(--color-muted);
  border: 1px solid var(--color-line);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.4);
  font-size: 12px;
  font-weight: 500;
}

.filter.is-active {
  color: white;
  border-color: var(--color-olive);
  background: var(--color-olive);
}
</style>
